import styled from '@emotion/styled'
import React, { useState } from 'react'
import { Controller, useFieldArray } from 'react-hook-form'

import chevron from 'src/static/chevron-down.svg'

import { Input, Text } from '../../../components'

const DOMAIN_REGEX = /^@(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9][a-z0-9-]{0,61}[a-z0-9]$/

const Container = styled.div`
  border: 1px dashed #e1e3ea;
  border-radius: 10px;
  margin: 10px 0;
  padding: 10px;
`

const Title = styled.label<{ collapsed: boolean }>`
  cursor: pointer;
  margin: 0;
  position: relative;
  width: 100%;

  &:after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 24px;
    height: 24px;
    background: url(${chevron}) no-repeat center / 12px 12px;
    transform: ${({ collapsed }) => (collapsed ? 'rotate(0deg)' : 'rotate(180deg)')};
    transition: all 200ms;
  }
`

const List = styled.div<{ collapsed: boolean }>`
  border-top: 1px solid #e1e3ea;
  display: ${({ collapsed }) => (collapsed ? 'none' : 'block')};
  margin-top: 10px;
  padding-top: 10px;
`

const Item = styled.div`
  align-items: center;
  display: flex;
  margin-bottom: 24px;

  > div {
    height: 40px;
    margin: 0 5px;

    > input {
      padding-top: 10px;
    }

    > p {
      margin: 0;
      padding-top: 5px;
    }
  }
`

const AddButton = styled(Text)`
  cursor: pointer;
  margin: 0;
  margin-top: 10px;
  text-decoration: underline;
  width: fit-content;
`

const DeleteButton = styled(Text)`
  color: red;
  cursor: pointer;
  margin: 0 10px;
  width: fit-content;
`

interface IProps {
  control: any
  errors: any
}

function ValidEmailDomains({ control, errors }: IProps) {
  const [collapsed, setCollapsed] = useState(true)
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'agency.email_domain_validations',
  })

  return (
    <Container>
      <Title collapsed={collapsed} onClick={() => setCollapsed(!collapsed)}>
        Email domain validation
      </Title>
      <List collapsed={collapsed}>
        {fields.map((item, index) => (
          <Item key={item.id}>
            <Controller
              as={Input}
              name={`agency.email_domain_validations[${index}].value`}
              control={control}
              error={errors.agency?.email_domain_validations?.[index]?.value}
              rules={{
                validate: {
                  beginsWith: (value) => value[0] === '@' || 'Domain must begin with @',
                  validDomain: (value) => DOMAIN_REGEX.test(value) || 'Invalid domain',
                  unique: (value) =>
                    !fields.some((f) => f.id !== item.id && f.value === value) ||
                    'Domain is already added',
                },
              }}
              defaultValue={item.value || ''}
            />
            <DeleteButton onClick={() => remove(index)}>Delete</DeleteButton>
          </Item>
        ))}
        <AddButton onClick={() => append({ value: '' })}>+ Add</AddButton>
      </List>
    </Container>
  )
}

export default ValidEmailDomains
